import React from 'react'
import { useIntl } from 'react-intl'
import { Tooltip, Icon } from '@mui/material'
import { withStyles } from '@mui/styles'

import { fileSizeTransform } from '../logics/file-size-transform.logics'
import { uploadTooltipStyles } from './upload-tooltip.styles'

/**
 * @typedef {object} UploadTooltipProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {object} file - Objeto con configuración del archivo a subir.
 * @property {Array<string>} [file.accept] - Array de tipos MIME aceptados.
 * @property {number} [file.maxSize] - Tamaño máximo del archivo en bytes.
 */

/**
 * Tooltip informativo para campos de carga de archivos.
 *
 * Muestra un tooltip con ícono de ayuda que indica el tamaño máximo permitido y los tipos
 * de archivo aceptados. Formatea el tamaño de archivo automáticamente (KB, MB, etc.) y
 * extrae las extensiones de los tipos MIME. Solo se renderiza si hay configuración de
 * accept o maxSize.
 *
 * @component
 * @example
 * <UploadTooltip
 *   file={{
 *     accept: ['image/png', 'image/jpeg', 'application/pdf'],
 *     maxSize: 5242880 // 5 MB
 *   }}
 * />
 *
 * @param {UploadTooltipProps} props - Las props del componente UploadTooltip.
 */
export const UploadTooltip = withStyles(uploadTooltipStyles)(({ classes, file, ...props }) => {
  const { formatMessage } = useIntl()

  if (!file) return null
  if (!file.accept && !file.maxSize) return null

  return (
    <Tooltip
      data-role="upload tooltip"
      title={
        <div data-role="upload info">
          <div className={classes.container}>
            <span className={classes.title}>
              {formatMessage({ id: 'documents.tooltip.maximumSize' })}
            </span>
            <span className={classes.description} data-role="size info">
              {fileSizeTransform(file.maxSize)}
            </span>
          </div>
          <div className={classes.container}>
            <span className={classes.title}>
              {formatMessage({ id: 'documents.tooltip.acceptedFiles' })}
            </span>
            {file.accept ? (
              <span className={classes.description} data-role="type info">
                {file.accept.map((type) => type.split('/').pop()).join(', ')}
              </span>
            ) : null}
          </div>
        </div>
      }
    >
      <span style={{ fontSize: 16, marginLeft: 4 }} data-role="upload help label">
        <Icon fontSize="inherit">help_outline</Icon>
      </span>
    </Tooltip>
  )
})
